import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Sparkles } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody } from '../components/ui/Card'
import { Dialog } from '../components/ui/Dialog'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Label, Select, Textarea } from '../components/ui/Input'
import { apiClient } from '../lib/api'
import { formatRelative } from '../lib/utils'

type Pattern = {
  id: string
  category: string
  title: string
  problem: string
  solution: string
  tags: string[]
  timesUsed: number
  lastUsedAt: string | null
}

type SearchResult = { results: { pattern: Pattern; score: number }[]; total: number }

export function PatternsPage() {
  const qc = useQueryClient()
  const [query, setQuery] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['patterns', { query }],
    queryFn: () =>
      apiClient.get<SearchResult>(
        `/patterns/search?q=${encodeURIComponent(query)}${query ? '' : '&limit=50'}`,
      ),
  })

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.post('/patterns', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patterns'] })
      setDialogOpen(false)
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const body = Object.fromEntries(fd.entries()) as Record<string, unknown>
    if (typeof body.tags === 'string') {
      body.tags = (body.tags as string)
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
    }
    save.mutate(body)
  }

  return (
    <AppLayout>
      <PageHeader
        title="Patterns"
        description="Knowledge base self-learning del team"
        action={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="size-4" /> Nuovo pattern
          </Button>
        }
      />

      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500 pointer-events-none" />
        <Input
          placeholder="Cerca pattern (problema, soluzione, tag)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
            <Card key={i}>
              <CardBody>
                <Skeleton className="h-5 w-1/2 mb-3" />
                <Skeleton className="h-4 w-full mb-1" />
                <Skeleton className="h-4 w-2/3" />
              </CardBody>
            </Card>
          ))}
        </div>
      ) : data && data.results.length > 0 ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {data.results.map(({ pattern: p, score }) => (
            <Card key={p.id} className="hover:border-zinc-700 transition">
              <CardBody>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="info">{p.category}</Badge>
                      {p.timesUsed > 0 && <Badge variant="muted">{p.timesUsed} usi</Badge>}
                    </div>
                    <h3 className="mt-2 text-base font-semibold text-zinc-50">{p.title}</h3>
                  </div>
                  {query && (
                    <div className="text-xs text-zinc-500 tabular-nums">
                      {(score * 100).toFixed(0)}
                    </div>
                  )}
                </div>
                <p className="mt-3 text-sm text-zinc-400 line-clamp-2">{p.problem}</p>
                <details className="mt-3">
                  <summary className="text-xs text-cyan-400 cursor-pointer hover:text-cyan-300">
                    Mostra soluzione
                  </summary>
                  <p className="mt-2 text-sm text-zinc-300 whitespace-pre-wrap">{p.solution}</p>
                </details>
                <div className="mt-4 flex items-center justify-between gap-2 text-xs text-zinc-500">
                  <div className="flex flex-wrap gap-1">
                    {p.tags.slice(0, 4).map((t) => (
                      <span key={t} className="rounded bg-zinc-800/60 px-1.5 py-0.5">
                        {t}
                      </span>
                    ))}
                  </div>
                  {p.lastUsedAt && <span>usato {formatRelative(p.lastUsedAt)}</span>}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardBody>
            <Empty
              icon={query ? Search : Sparkles}
              title={query ? 'Nessun pattern trovato' : 'Nessun pattern ancora'}
              description={
                query
                  ? `Nessun match per "${query}". Prova con altri termini o crea il pattern.`
                  : 'Salva i problemi non banali che hai risolto. La prossima volta che li incontri, li trovi qui.'
              }
              action={
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="size-4" /> Nuovo pattern
                </Button>
              }
            />
          </CardBody>
        </Card>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Nuovo pattern"
        description="Cattura un problema/soluzione non banale per riuso futuro."
        size="lg"
      >
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label htmlFor="title">Titolo *</Label>
              <Input id="title" name="title" required minLength={5} maxLength={200} />
            </div>
            <div>
              <Label htmlFor="category">Categoria</Label>
              <Select id="category" name="category" defaultValue="altro">
                <option value="sviluppo">Sviluppo</option>
                <option value="configurazione">Configurazione</option>
                <option value="cliente">Cliente</option>
                <option value="processo">Processo</option>
                <option value="architettura">Architettura</option>
                <option value="devops">DevOps</option>
                <option value="sicurezza">Sicurezza</option>
                <option value="altro">Altro</option>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="problem">Problema *</Label>
            <Textarea
              id="problem"
              name="problem"
              required
              minLength={10}
              placeholder="Cosa stava succedendo, errori, sintomi…"
            />
          </div>
          <div>
            <Label htmlFor="solution">Soluzione *</Label>
            <Textarea
              id="solution"
              name="solution"
              required
              minLength={10}
              placeholder="Cosa ha risolto, step by step…"
            />
          </div>
          <div>
            <Label htmlFor="tags">Tag (separati da virgola)</Label>
            <Input id="tags" name="tags" placeholder="docker, nginx, ssl" />
          </div>
          {save.isError && (
            <div className="text-sm text-red-400">{(save.error as Error)?.message ?? 'Errore'}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Salvataggio…' : 'Salva pattern'}
            </Button>
          </div>
        </form>
      </Dialog>
    </AppLayout>
  )
}
